import { type Static, Type } from "@sinclair/typebox"
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema } from "./common"

export const ArtworkVariantKindSchema = Type.Union([
  Type.Literal("card_art"),
  Type.Literal("thumb"),
])

export type ArtworkVariantKind = Static<typeof ArtworkVariantKindSchema>

export const ArtworkAssetVariantSchema = Type.Object({
  kind: ArtworkVariantKindSchema,
  assetId: IdSchema,
  mimeType: Type.Literal("image/webp"),
  width: Type.Integer({
    minimum: 1,
  }),
  height: Type.Integer({
    minimum: 1,
  }),
  byteLength: Type.Integer({
    minimum: 1,
  }),
})

export type ArtworkAssetVariant = Static<typeof ArtworkAssetVariantSchema>

export const ArtworkUploadStatusSchema = Type.Union([
  Type.Literal("processing"),
  Type.Literal("ready"),
  Type.Literal("rejected"),
  Type.Literal("pending_review"),
])

export type ArtworkUploadStatus = Static<typeof ArtworkUploadStatusSchema>

export const ArtworkFocalPointSchema = Type.Object({
  x: Type.Number({
    minimum: 0,
    maximum: 1,
  }),
  y: Type.Number({
    minimum: 0,
    maximum: 1,
  }),
})

export type ArtworkFocalPoint = Static<typeof ArtworkFocalPointSchema>

export const CardArtworkSchema = Type.Object({
  hash: NonEmptyStringSchema,
  focalPoint: Type.Optional(ArtworkFocalPointSchema),
  zoom: Type.Optional(
    Type.Number({
      minimum: 1,
      maximum: 2,
    }),
  ),
  variants: Type.Array(ArtworkAssetVariantSchema, {
    minItems: 1,
    maxItems: 2,
  }),
})

export type CardArtwork = Static<typeof CardArtworkSchema>

export const ArtworkUploadRecordSchema = Type.Object({
  uploadId: IdSchema,
  originalFilename: NonEmptyStringSchema,
  status: ArtworkUploadStatusSchema,
  mimeType: Type.String({
    minLength: 1,
  }),
  byteLength: Type.Integer({
    minimum: 1,
  }),
  hash: Type.Optional(NonEmptyStringSchema),
  variants: Type.Optional(
    Type.Array(ArtworkAssetVariantSchema, {
      minItems: 1,
      maxItems: 2,
    }),
  ),
  rejectionReason: Type.Optional(NonEmptyStringSchema),
})

export type ArtworkUploadRecord = Static<typeof ArtworkUploadRecordSchema>

export const ArtworkAssetRecordSchema = Type.Object({
  assetId: IdSchema,
  kind: ArtworkVariantKindSchema,
  mimeType: Type.Literal("image/webp"),
  width: Type.Integer({
    minimum: 1,
  }),
  height: Type.Integer({
    minimum: 1,
  }),
  byteLength: Type.Integer({
    minimum: 1,
  }),
  dataUrl: NonEmptyStringSchema,
  createdAt: IsoTimestampSchema,
})

export type ArtworkAssetRecord = Static<typeof ArtworkAssetRecordSchema>

export const ArtworkUploadEnvelopeSchema = Type.Object({
  artwork: CardArtworkSchema,
  assets: Type.Array(ArtworkAssetRecordSchema, {
    minItems: 1,
    maxItems: 2,
  }),
})

export type ArtworkUploadEnvelope = Static<typeof ArtworkUploadEnvelopeSchema>

export const ArtworkUploadResponseSchema = Type.Object({
  artwork: CardArtworkSchema,
})

export type ArtworkUploadResponse = Static<typeof ArtworkUploadResponseSchema>
